import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from './useWorkspace';
import { useAuth } from './useAuth';
import { isCircuitOpen } from '@/lib/circuit-breaker';
import { isGcalMultiCalendarEnabled } from '@/lib/gcal-feature-flags';

interface GoogleCalendarStatus {
  needsReconnect: boolean;
  email: string | null;
  isChecking: boolean;
  hasConnection: boolean;
}

export function useGoogleCalendarStatus() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const [status, setStatus] = useState<GoogleCalendarStatus>({
    needsReconnect: false,
    email: null,
    isChecking: true,
    hasConnection: false,
  });

  const checkStatus = useCallback(async () => {
    if (!workspaceId) {
      setStatus(prev => ({ ...prev, isChecking: false, hasConnection: false }));
      return;
    }

    if (isCircuitOpen()) {
      console.log("[GoogleCalendar] Circuit breaker OPEN, skipping check");
      return;
    }

    try {
      if (isGcalMultiCalendarEnabled()) {
        const { data: credentials, error: credError } = await supabase
          .from('google_calendar_credentials')
          .select('id, email, is_active, needs_reconnect' as any)
          .eq('workspace_id', workspaceId)
          .eq('is_active', true)
          .order('created_at', { ascending: true });

        if (credError || !credentials?.length) {
          setStatus({
            needsReconnect: false,
            email: null,
            isChecking: false,
            hasConnection: false,
          });
          return;
        }

        const { count } = await supabase
          .from('google_calendar_sync_calendars')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId)
          .eq('is_enabled', true);

        setStatus({
          needsReconnect: (credentials as any[]).some((c: any) => c.needs_reconnect === true),
          email: (credentials[0] as any).email,
          isChecking: false,
          hasConnection: (count ?? 0) > 0 || credentials.length > 0,
        });
        return;
      }

      const { data: credentials, error: credError } = await supabase
        .from('google_calendar_credentials')
        .select('id, email, is_active, needs_reconnect' as any)
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        .maybeSingle();

      if (credError || !credentials) {
        setStatus({
          needsReconnect: false,
          email: null,
          isChecking: false,
          hasConnection: false,
        });
        return;
      }

      const cred = credentials as any;

      setStatus({
        needsReconnect: cred.needs_reconnect === true,
        email: cred.email,
        isChecking: false,
        hasConnection: true,
      });
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
      setStatus(prev => ({ ...prev, isChecking: false }));
    }
  }, [workspaceId]);

  useEffect(() => {
    checkStatus();

    // Re-check every 15 minutes (lightweight SELECT only)
    const interval = setInterval(checkStatus, 15 * 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [checkStatus]);

  return { ...status, refresh: checkStatus };
}
